import useSEO from "@/hooks/useSEO";
import { useState, useMemo, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Phone,
  X,
  Search,
  Flame,
  Wheat,
  Sparkles,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { MENU, CATEGORIES, ALLERGENS_NOTE } from "@/data/menu";

const fmtPrice = (n) => n.toFixed(2).replace(".", ",") + " €";

// Decorative olive branch — pure SVG
function OliveBranch({ className = "", flipped = false }) {
  return (
    <svg
      viewBox="0 0 80 24"
      className={className}
      style={{ transform: flipped ? "scaleX(-1)" : undefined }}
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M2 12 Q 20 6, 40 12 T 78 12"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinecap="round"
      />
      {[14, 22, 30, 38, 46, 54, 62].map((x, i) => {
        const above = i % 2 === 0;
        return (
          <ellipse
            key={x}
            cx={x}
            cy={above ? 8 : 16}
            rx="3"
            ry="1.5"
            transform={`rotate(${above ? -25 : 25} ${x} ${above ? 8 : 16})`}
            fill="currentColor"
            opacity="0.7"
          />
        );
      })}
      <circle cx="2" cy="12" r="1.5" fill="currentColor" />
    </svg>
  );
}

// Greek key (meander) pattern — removed by user request

function DishCard({ dish, onOpen }) {
  return (
    <button
      onClick={onOpen}
      data-testid={`dish-${dish.id}`}
      className="dish-card group relative text-left w-full bg-[var(--niko-cream)] border border-[var(--niko-charcoal)]/10 rounded-sm overflow-hidden transition-all duration-500 hover:border-[var(--niko-gold)] hover:-translate-y-1"
    >
      {dish.photo && (
        <div className="relative aspect-[16/10] overflow-hidden">
          <img
            src={dish.photo}
            alt={dish.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        </div>
      )}

      <div className="relative p-7 md:p-8 flex flex-col h-full">
        {/* top row: olive accent + featured badge */}
        <div className="relative z-10 flex items-start justify-between mb-5">
          <OliveBranch className="w-10 h-4 text-[var(--niko-gold)]" />
          {dish.featured && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[var(--niko-cream-2)] text-[var(--niko-blue)] text-[9px] tracking-[0.2em] uppercase font-semibold border border-[var(--niko-gold)]/40 shadow-sm">
              <Flame className="w-2.5 h-2.5 text-[var(--niko-gold)]" />
              Beliebt
            </span>
          )}
        </div>

        <h3 className="font-display text-2xl leading-[1.15] text-[var(--niko-charcoal)]">
          {dish.name}
        </h3>

        <p className="mt-3 font-greek italic text-[15px] leading-snug text-[var(--niko-charcoal)]/65 line-clamp-3">
          {dish.desc}
        </p>

        {/* Spacer pushes the footer down */}
        <div className="flex-1 min-h-[12px]" />

        {/* dotted leader + price */}
        <div className="mt-6 flex items-baseline gap-3">
          <span className="text-[10px] tracking-[0.24em] uppercase text-[var(--niko-charcoal)]/45 whitespace-nowrap">
            Preis
          </span>
          <span
            aria-hidden="true"
            className="flex-1 border-b border-dotted border-[var(--niko-charcoal)]/25 translate-y-[-3px]"
          />
          <span className="font-display text-2xl tabular-nums text-[var(--niko-blue)]">
            {fmtPrice(dish.price)}
          </span>
        </div>

        {/* hover reveal */}
        <div className="mt-5 flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[var(--niko-gold)] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <Sparkles className="w-3 h-3" />
          Details ansehen
        </div>
      </div>
    </button>
  );
}

export default function Speisekarte() {
  useSEO({
    title: "Speisekarte · Niko's Grill Bochum-Höntrop",
    description:
      "Die Speisekarte von Niko's Grill in Bochum: Gyros, Souvlaki, Grillteller, Vorspeisen & griechische Spezialitäten vom Holzkohlegrill. Jetzt entdecken!",
    path: "/speisekarte",
  });

  const [active, setActive] = useState(CATEGORIES[0].id);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(null);
  const tabsRef = useRef(null);

  const filtered = useMemo(() => {
    if (!query.trim()) return MENU[active] || [];
    const q = query.toLowerCase();
    return Object.values(MENU)
      .flat()
      .filter(
        (d) =>
          d.name.toLowerCase().includes(q) ||
          d.desc.toLowerCase().includes(q)
      );
  }, [active, query]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const activeCat = CATEGORIES.find((c) => c.id === active);

  return (
    <main className="bg-[var(--niko-cream)]" data-testid="speisekarte-page">
      <Navbar />

      {/* Editorial hero */}
      <section
        className="relative pt-36 pb-16 md:pt-44 md:pb-24 bg-[var(--niko-dark)] text-white overflow-hidden"
        data-testid="menu-hero"
      >
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            background:
              "radial-gradient(60% 60% at 20% 30%, var(--niko-gold), transparent 60%), radial-gradient(60% 60% at 80% 70%, var(--niko-blue), transparent 60%)",
          }}
        />
        <div className="grain pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-white/65 hover:text-[var(--niko-gold)] transition-colors mb-10"
            data-testid="back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück
          </Link>

          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-4 mb-7 text-[var(--niko-gold)]">
                <OliveBranch className="w-12 h-4" />
                <span className="text-[11px] tracking-[0.4em] uppercase">
                  Speisekarte
                </span>
                <OliveBranch className="w-12 h-4" flipped />
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02]">
                Die ganze <br />
                <span className="italic text-[var(--niko-gold)]">Karte</span>.
              </h1>
              <p className="mt-7 max-w-xl font-greek italic text-lg md:text-xl text-white/75 leading-relaxed">
                Über 100 Gerichte & Getränke aus 44 Jahren griechischer
                Tradition in Höntrop — klicken Sie auf jedes Gericht für Details.
              </p>
            </div>

            <div className="lg:col-span-4">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Gericht suchen…"
                  className="input-dark w-full pl-11 pr-4 py-3.5 rounded-full text-sm"
                  data-testid="menu-search"
                />
              </div>
              <p className="mt-4 text-[10px] tracking-[0.28em] uppercase text-white/40 text-right">
                {Object.values(MENU).flat().length} Gerichte ·{" "}
                {CATEGORIES.length} Kategorien
              </p>
            </div>
          </div>

          <div className="mt-12 flex items-center justify-center gap-5 text-[var(--niko-gold)]/50">
            <OliveBranch className="w-20 h-4" />
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--niko-gold)]/60" />
            <OliveBranch className="w-20 h-4" flipped />
          </div>
        </div>
      </section>

      {/* Sticky category tabs */}
      <div
        ref={tabsRef}
        className="sticky top-[64px] z-30 bg-[var(--niko-cream)]/95 backdrop-blur-md border-b border-[var(--niko-charcoal)]/8"
        data-testid="menu-tabs"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex gap-2 overflow-x-auto no-scrollbar py-4">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => {
                  setActive(c.id);
                  setQuery("");
                  // smooth scroll to top of dishes when category changes
                  setTimeout(() => {
                    document
                      .getElementById("dishes-anchor")
                      ?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }, 50);
                }}
                data-testid={`tab-${c.id}`}
                className={`shrink-0 px-5 py-2.5 rounded-full text-[11px] tracking-[0.18em] uppercase font-semibold whitespace-nowrap transition-all ${
                  active === c.id && !query
                    ? "bg-[var(--niko-charcoal)] text-white"
                    : "bg-[var(--niko-charcoal)]/5 text-[var(--niko-charcoal)]/70 hover:bg-[var(--niko-charcoal)]/10"
                }`}
              >
                {c.title}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Dish content */}
      <section
        id="dishes-anchor"
        className="scroll-mt-32 pt-20 md:pt-28 pb-20 md:pb-28 px-6 lg:px-10"
      >
        <div className="max-w-7xl mx-auto">
          {!query && activeCat && (
            <header className="mb-14 md:mb-16 text-center max-w-2xl mx-auto">
              <div className="flex items-center justify-center gap-5 text-[var(--niko-gold)] mb-6">
                <OliveBranch className="w-14 h-4" />
                <span className="text-[10px] tracking-[0.4em] uppercase font-semibold">
                  Kategorie
                </span>
                <OliveBranch className="w-14 h-4" flipped />
              </div>
              <h2 className="font-display text-4xl md:text-5xl text-[var(--niko-charcoal)] leading-tight">
                {activeCat.title}
              </h2>
              <p className="mt-5 font-greek italic text-lg text-[var(--niko-charcoal)]/65 leading-relaxed">
                {activeCat.blurb}
              </p>
              <div className="mt-6 flex items-center justify-center gap-3">
                <span className="h-px w-12 bg-[var(--niko-gold)]/40" />
                <span className="text-[10px] tracking-[0.32em] uppercase text-[var(--niko-charcoal)]/45 tabular-nums">
                  {(MENU[active] || []).length} Gerichte
                </span>
                <span className="h-px w-12 bg-[var(--niko-gold)]/40" />
              </div>
            </header>
          )}

          {query && (
            <p className="mb-12 text-center font-greek italic text-xl text-[var(--niko-charcoal)]/70">
              {filtered.length}{" "}
              {filtered.length === 1 ? "Treffer" : "Treffer"} für „
              <span className="text-[var(--niko-blue)] not-italic">{query}</span>"
            </p>
          )}

          {filtered.length === 0 ? (
            <p className="text-center py-20 font-greek italic text-2xl text-[var(--niko-charcoal)]/45">
              Keine Treffer — bitte anderen Begriff probieren.
            </p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-7">
              {filtered.map((d) => (
                <DishCard
                  key={d.id}
                  dish={d}
                  onOpen={() => setOpen(d)}
                />
              ))}
            </div>
          )}

          {/* Footer note */}
          <div className="mt-20 max-w-3xl mx-auto text-center">
            <div className="flex items-center justify-center gap-4 text-[var(--niko-gold)] mb-6">
              <OliveBranch className="w-16 h-4" />
              <Wheat className="w-4 h-4" />
              <OliveBranch className="w-16 h-4" flipped />
            </div>
            <p className="font-greek italic text-base md:text-lg text-[var(--niko-charcoal)]/65 leading-relaxed">
              {ALLERGENS_NOTE}
            </p>
            <a
              href="tel:+492327995454"
              data-testid="menu-call"
              className="mt-10 inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[var(--niko-charcoal)] text-white text-xs font-semibold tracking-[0.22em] uppercase hover:bg-[var(--niko-blue)] transition-colors"
            >
              <Phone className="w-4 h-4" />
              Bestellung: +49 2327 995454
            </a>
          </div>
        </div>
      </section>

      {/* Dish dialog */}
      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-end md:items-center justify-center"
          data-testid="dish-dialog"
          onClick={() => setOpen(null)}
        >
          <div className="absolute inset-0 bg-[var(--niko-dark)]/85 backdrop-blur-sm animate-in fade-in" />
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full md:max-w-xl bg-[var(--niko-cream)] md:rounded-sm overflow-hidden shadow-2xl max-h-[92vh] flex flex-col animate-in slide-in-from-bottom-8 md:zoom-in-95 duration-300"
          >
            <button
              onClick={() => setOpen(null)}
              data-testid="dialog-close"
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-[var(--niko-dark)]/85 text-white flex items-center justify-center hover:bg-[var(--niko-gold)] hover:text-[var(--niko-dark)] transition-colors"
              aria-label="Schließen"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Decorative top stripe */}
            <div className="relative bg-[var(--niko-dark)] text-[var(--niko-gold)] px-7 md:px-10 py-7 overflow-hidden">
              <div className="absolute inset-0 grain pointer-events-none" />
              <div className="relative flex items-center gap-4 mb-2">
                <OliveBranch className="w-10 h-3" />
                <span className="text-[10px] tracking-[0.4em] uppercase">
                  Niko's Grill
                </span>
                <OliveBranch className="w-10 h-3" flipped />
              </div>
              {open.featured && (
                <div className="absolute top-7 right-16">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[var(--niko-gold)] text-[var(--niko-dark)] text-[10px] tracking-[0.18em] uppercase font-semibold">
                    <Flame className="w-3 h-3" />
                    Hausfavorit
                  </span>
                </div>
              )}
            </div>

            {open.photo && (
              <div className="relative shrink-0 max-h-[38vh] overflow-hidden">
                <img
                  src={open.photo}
                  alt={open.name}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div className="p-7 md:p-10 overflow-y-auto">
              <h2 className="font-display text-3xl md:text-4xl text-[var(--niko-charcoal)] leading-tight">
                {open.name}
              </h2>

              <p className="mt-6 font-greek italic text-lg text-[var(--niko-charcoal)]/75 leading-[1.6]">
                {open.desc}
              </p>

              <div className="mt-8 flex items-center justify-center gap-4 text-[var(--niko-gold)]/45">
                <OliveBranch className="w-16 h-4" />
                <span className="w-1 h-1 rounded-full bg-[var(--niko-gold)]/50" />
                <OliveBranch className="w-16 h-4" flipped />
              </div>

              <div className="mt-7 flex items-baseline gap-3">
                <span className="text-[10px] tracking-[0.28em] uppercase text-[var(--niko-charcoal)]/45 whitespace-nowrap">
                  Preis
                </span>
                <span
                  aria-hidden="true"
                  className="flex-1 border-b border-dotted border-[var(--niko-charcoal)]/30 translate-y-[-4px]"
                />
                <span className="font-display text-4xl tabular-nums text-[var(--niko-blue)]">
                  {fmtPrice(open.price)}
                </span>
              </div>

              <a
                href="tel:+492327995454"
                data-testid="dialog-call"
                className="mt-9 inline-flex w-full items-center justify-center gap-3 px-6 py-4 rounded-full bg-[var(--niko-gold)] text-[var(--niko-dark)] text-sm font-semibold tracking-[0.18em] uppercase hover:bg-[var(--niko-gold-soft)] transition-colors"
              >
                <Phone className="w-4 h-4" />
                Bestellen: +49 2327 995454
              </a>
              {!open.photo && (
                <p className="mt-4 text-[10px] text-[var(--niko-charcoal)]/45 text-center leading-relaxed">
                  Foto-Darstellung folgt — bei Fragen zur Zubereitung rufen Sie
                  uns gerne an.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </main>
  );
}
